import { CloseCircleFilled, SearchOutlined, StarOutlined } from '@ant-design/icons';
import { Keyboard } from '@capacitor/keyboard';
import { useDebounceFn } from 'ahooks';
import PostsList from 'components/PostsList';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Skeleton } from 'components/ui/skeleton';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CategoryPageDTO, CategorySummaryDTO } from 'services/api/models/CategoryDTO';
import { contentActions } from 'store/content';
import { selectSuggestedPosts } from 'store/content/selectors';
import Platform from 'utils/Platform';
import { CategoryIconBox } from './CategoryIcon';
import CategoryRows from './CategoryRows';
import InfoSearchResults from './InfoSearchResults';
import { openCategory } from './navigation';
import { usePostSearch } from './queries';

/** Articles of the personalised list shown on the home */
const FOR_YOU_SIZE = 3;

/** The home of the categorised Infos page: search, the editorial card, her favourites, her topics and a few articles for her, then the whole map. */
export default function InfoHome({ root }: { root: CategoryPageDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const query = searchParams.get('search') || '';
  const [searchValue, setSearchValue] = useState(query);

  // The same personalised list as the home page, kept in the store for the
  // session (see ArticlesList for why): fetched once, when the store is empty
  const suggestedPosts = useSelector(selectSuggestedPosts);
  const { data: fetchedPosts, isPending: loadingSuggestedPosts } = usePostSearch(undefined);
  useEffect(() => {
    if (!suggestedPosts && fetchedPosts) dispatch(contentActions.setSuggestedPosts(fetchedPosts));
  }, [suggestedPosts, fetchedPosts, dispatch]);

  const setQuery = (search: string) => {
    // A debounced update can fire after she has left the page (a tap on a
    // row within the delay): the URL change would then replace the
    // navigation under way and bring her back here
    if (window.location.pathname !== location.pathname) return;
    setSearchParams(
      prev => {
        const params = createSearchParams(prev);
        if (search.trim()) {
          params.set('search', search.trim());
        } else {
          params.delete('search');
        }
        return params;
      },
      // Typing refines one search: no history entry per keystroke
      { replace: true },
    );
  };
  // The search runs as she types, a pause after the last key
  const { run: searchAsSheTypes, cancel: cancelPendingSearch } = useDebounceFn(setQuery, { wait: 400 });
  const triggerSearch = (search: string) => {
    cancelPendingSearch();
    setQuery(search);
    if (Platform.isNative()) {
      Keyboard.hide();
    }
  };
  const clearSearch = () => {
    setSearchValue('');
    triggerSearch('');
  };

  // Sensitive categories (violence) are never suggested but always findable:
  // ordinary rows of the map, kept at the very bottom
  const areas = root.items.flatMap(item => (item.type === 'category' ? [item.category] : []));
  const allTopics = [...areas.filter(area => !area.is_sensitive), ...areas.filter(area => area.is_sensitive)];
  const yourTopics = root.suggested_categories ?? [];
  const favourites = root.bookmarked_categories ?? [];
  const forYouPosts = (suggestedPosts ?? []).slice(0, FOR_YOU_SIZE);
  // Her topics and a few articles for her, under one heading
  const showRecommended = yourTopics.length > 0 || forYouPosts.length > 0 || loadingSuggestedPosts;

  return (
    <div className="flex flex-col gap-9 pb-8">
      <div className="relative w-full">
        <Input
          type="search"
          // room for the clear + search buttons; the native clear button would duplicate ours
          className="pr-20 [&::-webkit-search-cancel-button]:hidden"
          placeholder={t('infoPage.structure.searchPlaceholder')}
          value={searchValue}
          onChange={e => {
            setSearchValue(e.target.value);
            searchAsSheTypes(e.target.value);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') triggerSearch(searchValue);
          }}
          autoCorrect="on"
          enterKeyHint="search"
        />
        <div className="absolute inset-y-0 right-1 flex items-center gap-0.5">
          {searchValue && (
            <Button type="button" variant="ghost" size="icon" className="hover:bg-transparent" onClick={clearSearch}>
              <CloseCircleFilled />
            </Button>
          )}
          <Button type="button" variant="ghost" size="icon" onClick={() => triggerSearch(searchValue)}>
            <SearchOutlined />
          </Button>
        </div>
      </div>
      {query ? (
        <InfoSearchResults query={query} />
      ) : (
        <>
          {/* What she keeps comes first, before what we suggest */}
          <section className="flex flex-col gap-3.5" data-testid="favourites">
            <h3>{t('infoPage.structure.favourites')}</h3>
            {favourites.length > 0 ? (
              <CategoryRows categories={favourites} openedFrom="info-favourites" testIdPrefix="favourite" />
            ) : (
              // A quiet placeholder: it only says where favourites will go
              <p
                className="flex items-center gap-2 rounded-lg border border-dashed px-3.5 py-2.5 text-sm text-subtle-foreground"
                data-testid="favourites-empty"
              >
                <StarOutlined />
                <span>{t('infoPage.structure.favouritesEmpty')}</span>
              </p>
            )}
          </section>
          {showRecommended && (
            <section className="flex flex-col gap-3.5" data-testid="recommended">
              <h3>{t('infoPage.structure.recommended')}</h3>
              {yourTopics.length > 0 && (
                <div className="grid grid-cols-2 gap-2.5" data-testid="your-topics">
                  {yourTopics.map((topic: CategorySummaryDTO, index) => {
                    const open = () => openCategory(navigate, topic.slug, 'info-your-topics');
                    return (
                      <Card
                        key={topic.id}
                        // A tinted tile with a white icon box, so her topics stand apart from the article rows
                        className="min-h-28 cursor-pointer bg-muted"
                        data-testid={`your-topic-${index}`}
                        onClick={open}
                        onKeyDown={e => {
                          if (e.key === 'Enter') open();
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <CardContent className="flex flex-1 flex-col gap-2">
                          <CategoryIconBox category={topic} size="sm" surface="card" />
                          <h4 className="text-base">{topic.name}</h4>
                          {/* The count sits at the bottom of the tile */}
                          {topic.show_count && (
                            <span className="mt-auto text-sm text-muted-foreground">
                              {t('infoPage.structure.articles', { count: topic.post_count })}
                              {topic.read_count > 0 &&
                                ` · ${t('infoPage.structure.read', { count: topic.read_count })}`}
                            </span>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
              {loadingSuggestedPosts && !suggestedPosts ? (
                <Skeleton className="h-16 w-full" />
              ) : (
                <PostsList
                  posts={forYouPosts}
                  posthogOnArticleOpenExtras={{ opened_on_page: 'info-for-you' }}
                  testIdPrefix="postinlist"
                />
              )}
            </section>
          )}
          <section className="flex flex-col gap-3.5">
            <h3>{t('infoPage.structure.allTopics')}</h3>
            <CategoryRows categories={allTopics} openedFrom="info-all-topics" testIdPrefix="area" />
          </section>
        </>
      )}
    </div>
  );
}
